// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CreateNewGroupModal from "@bg/App/Components/RfqRequestQuote/CreateNewGroupModal";
import setGroupWishListLineIsSelected from "@bg/App/Handlers/Pages/MyListDetails/SetGroupSelectedLines";
import copyLines from "@bg/App/Handlers/RequestQuote/CopyLines";
import deleteGroup from "@bg/App/Handlers/RequestQuote/DeleteGroup";
import moveLines from "@bg/App/Handlers/RequestQuote/MoveLines";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { parseBoolean } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, CartModel, QuoteLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface GroupActionsProps {
    groups: any;
    listGroups: string[];
    cartLines?: CartLineModel[] | QuoteLineModel[] | null;
    currentGroup: string;
    cart: CartModel;
    isQuote?: boolean;
    isExpired?: string;
}

const mapStateToProps = (state: CustomAppState) => ({
    selectedGroupLines: state.commonActions["selectedGroupLines"],
    uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
    addOnsGroupName: (getSettingsCollection(state).customSettings as any).addOnsGroupName as string,
    isQuoteDisabled: state.commonActions.isQuoteDisabled,
});

const mapDispatchToProps = {
    setGroupWishListLineIsSelected,
    deleteGroup,
    copyLines,
    moveLines,
};

type Props = GroupActionsProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface GroupActionsStyles {
    selectSortOption?: SelectPresentationProps;
    deleteGroupLink?: LinkPresentationProps;
    groupAddToCartButton?: ButtonPresentationProps;
    groupAcionsWrapper?: InjectableCss;
    selectAllGroup?: InjectableCss;
    moveToGroup?: InjectableCss;
    deleteGroup?: InjectableCss;
}

export const styles: GroupActionsStyles = {
    deleteGroupLink: linkIconStyleGenerator({
        icon: "Delete",
        textSize: "15px",
        iconSize: 16,
        additionalTypographyCss: css`
            opacity: 0.7;
            font-weight: ${fontConfig.weight.semiBold};
        `,
    }),
    groupAddToCartButton: {
        sizeVariant: "small",
        css: css`
            &&&,
            &&&:disabled {
                background: ${getColor("primary")};
                color: ${getColor("common.white")};
                border: 2px solid ${getColor("primary")};
            }
            &&&:hover:not(:disabled) {
                background: ${getColor("common.white")};
                color: ${getColor("primary")};
            }
        `,
    },
    groupAcionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            padding: 15px;
            @media print {
                display: none;
            }
            @media (max-width: 481px) {
                justify-content: space-between;
            }
            &&.with-borders {
                border-right: 3px solid ${getColor("common.ui3")};
                border-left: 3px solid ${getColor("common.ui3")};
            }
        `,
    },
    selectAllGroup: {
        css: css`
            margin-right: 30px;
            @media (max-width: 481px) {
                margin-bottom: 20px;
            }
        `,
    },
    moveToGroup: {
        css: css`
            display: flex;

            margin-right: 30px;
            &&.disabled svg,
            &&.disabled label {
                opacity: 0.5;
            }
            @media (max-width: 481px) {
                width: 100%;
                margin-right: 0;
                margin-bottom: 20px;
            }
            svg {
                margin-left: 5px;
            }
        `,
    },
    selectSortOption: {
        cssOverrides: {
            formField: css`
                flex-direction: row;
                align-items: center;
                width: 235px;

                label {
                    width: auto;
                    font-weight: ${fontConfig.weight.bold};
                    font-size: ${fontConfig.fontSize.small}px;
                    margin-right: 8px;
                    white-space: nowrap;
                }

                &&&& select:disabled {
                    color: ${getColor("text.main")};
                    opacity: 0.5;
                }

                @media (max-width: 481px) {
                    width: 100%;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
            inputSelect: css`
                font-size: ${fontConfig.fontSize.small}px;
                height: 32px;
            `,
        },
        iconProps: {
            css: css`
                &&&&&& {
                    right: 0;
                    top: 0;
                }
            `,
        },
    },
    deleteGroup: {
        css: css`
            @media (max-width: 853px) {
                margin-top: 10px;
                margin-right: 30px;
            }
            @media (max-width: 481px) {
                order: 10;
                margin: 0;
                display: flex;
                margin-top: 10px;
            }
            &&.disabled {
                pointer-events: none;
                cursor: not-allowed;
                opacity: 0.5;
            }
        `,
    },
};

const GroupActions: React.FC<Props> = ({
    groups,
    cartLines,
    currentGroup,
    listGroups,
    selectedGroupLines,
    cart,
    setGroupWishListLineIsSelected,
    uncategorizedGroupName,
    deleteGroup,
    copyLines,
    moveLines,
    isQuote,
    addOnsGroupName,
    isQuoteDisabled,
    toaster,
    isExpired,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [moveToGroup, setMoveToGroup] = useState("Choose a Group");
    const [copyToGroup, setCopyToGroup] = useState("Choose a Group");
    const [isDisabled, setIsDisabled] = useState(false);
    const [isMoveCopyDisabled, setIsMoveCopyDisabled] = useState(false);
    const [groupingModalIsOpen, setGroupingModalIsOpen] = useState({
        isOpen: false,
        shouldLoadCurrentCart: false,
    });
    const [isMove, setIsMove] = useState(false);
    const [deleteGroupModalIsOpen, setDeleteGroupModalIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isMovingLine, setIsMovingLine] = useState(false);
    const [isCopyingLine, setIsCopyingLine] = useState(false);

    useEffect(() => {
        if (
            (groups && !groups[currentGroup]?.length) ||
            !cartLines ||
            !cartLines.length ||
            (isQuoteDisabled && isQuoteDisabled.isDisabled)
        ) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
        if (
            !selectedGroupLines ||
            (selectedGroupLines && !selectedGroupLines[currentGroup]) ||
            (selectedGroupLines && selectedGroupLines[currentGroup] && !selectedGroupLines[currentGroup]?.length)
        ) {
            setIsMoveCopyDisabled(true);
        } else {
            setIsMoveCopyDisabled(false);
        }
    }, [cart, cartLines, selectedGroupLines, groups, isQuoteDisabled]);

    useEffect(() => {
        if (selectedGroupLines && selectedGroupLines?.hasOwnProperty(currentGroup)) {
            const lineIds: string[] = [];
            groups[currentGroup] && groups[currentGroup].forEach((line: any) => lineIds.push(line.lineId));

            if (
                groups[currentGroup] &&
                groups[currentGroup].every((line: any) => selectedGroupLines[currentGroup].indexOf(line.lineId) > -1)
            ) {
                setIsAllSelected(true);
            } else {
                setIsAllSelected(false);
            }
        }
    }, [selectedGroupLines]);

    const selectAllChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        const cartLineIds: string[] = [];
        groups[currentGroup].forEach((group: any) => {
            if (cartLines![group.index]) {
                cartLineIds.push(cartLines![group.index].id);
            }
        });
        setGroupWishListLineIsSelected({
            wishListLineIds: cartLineIds,
            groupName: currentGroup,
            isSelectAll: !isAllSelected,
        });
    };

    // move item to another group
    const handleSelectMoveToGroup = (e: React.FormEvent<HTMLSelectElement>) => {
        setIsMove(true);
        const newGroup = e.currentTarget.value;
        setMoveToGroup(newGroup);
        if (newGroup === "Create a New Group") {
            setIsCreatingNewGroup();
        } else {
            handleCopyOrMove(newGroup, true);
        }
    };

    // copy item to another group
    const handleSelectCopyToGroup = (e: React.FormEvent<HTMLSelectElement>) => {
        setIsMove(false);
        const newGroup = e.currentTarget.value;
        setCopyToGroup(newGroup);

        if (newGroup === "Create a New Group") {
            setIsCreatingNewGroup();
        } else {
            handleCopyOrMove(newGroup, false);
        }
    };

    // shared functionality for copy and move
    const handleCopyOrMove = (newGroup: string, isMoving?: boolean) => {
        // when choosing create a new group then closing the modal, the newGroup will be empty
        // in that case, set copyTo and moveTo to choose a group option
        let hasFixedQtyOnCopy = false;
        if (newGroup !== "Choose a Group" || !newGroup) {
            const linesModel: {
                lineId: string;
                sortOrder?: number;
                qtyOrdered: number;
                notes: string;
                parentId?: string;
            }[] = [];
            const ids: string[] = [];
            groups[currentGroup].forEach((item: any, index: number) => {
                const quoteLineItem = cartLines![item.index];
                const lineId = quoteLineItem.id;

                // Client request: if item has fixedQty and the quote status is porposed then don't copy the item between groups. See issue #BG-2292
                if (
                    isQuote &&
                    !isMoving &&
                    cart.statusDisplay === "QuoteProposed" &&
                    quoteLineItem.properties["isFixedQty"] === "true"
                ) {
                    hasFixedQtyOnCopy = true;
                    return;
                }

                if (
                    selectedGroupLines &&
                    selectedGroupLines?.hasOwnProperty(currentGroup) &&
                    selectedGroupLines[currentGroup].indexOf(lineId) > -1
                ) {
                    const groupItem = groups[currentGroup].filter((i: any) => i.lineId === lineId)[0];
                    ids.push(lineId);
                    const line = {
                        lineId,
                        sortOrder: groups[newGroup]
                            ? groups[newGroup].length + selectedGroupLines[currentGroup].indexOf(lineId) + 1
                            : linesModel.length + 1,
                        notes: groupItem.notes,
                        qtyOrdered: groupItem.qtyOrdered,
                    };
                    linesModel.push(line);

                    if (groupItem.relatedLinesIds?.length) {
                        const relatedErps = groupItem.relatedLinesIds.split(",");
                        const relatedItems =
                            cartLines &&
                            ((cartLines as any)!.filter((line: any) =>
                                relatedErps.some((x: string) => x === line.id),
                            ) as QuoteLineModel[]);

                        relatedItems &&
                            relatedItems.forEach((quoteline: QuoteLineModel, index: number) => {
                                const relatedProductGroup = JSON.parse(quoteline.properties.groups)?.filter(
                                    (group: any) => group.groupName === currentGroup,
                                )?.[0];
                                const relatedLine = {
                                    lineId: quoteline.id,
                                    qtyOrdered: relatedProductGroup.qtyOrdered!,
                                    notes: relatedProductGroup.notes,
                                    parentId: lineId,
                                };
                                linesModel.push(relatedLine);
                            });
                    }
                }
            });

            // Show warning toaster only once
            if (hasFixedQtyOnCopy) {
                toaster.addToast({ body: translate("Fixed Qty items cannot be copied"), messageType: "warning" });
            }

            // Clear toaster condition and selected group if no item is valid
            hasFixedQtyOnCopy = false;
            if (!linesModel || !linesModel.length) {
                setCopyToGroup("Choose a Group");
                return;
            }

            if (isMoving) {
                setIsMovingLine(true);
                moveLines({
                    cartId: cart.id,
                    originalGroupName: currentGroup,
                    newGroupName: newGroup,
                    linesModel,
                    reloadCart: !isQuote,
                    reloadQuote: isQuote,
                    onComplete: (result: any) => {
                        setIsMovingLine(false);

                        if (result.successful) {
                            setGroupWishListLineIsSelected({
                                wishListLineIds: ids,
                                groupName: currentGroup,
                                clearOnly: true,
                            });
                        }
                    },
                    onError: error => {
                        toasterContext.addToast({ body: translate(error), messageType: "danger" });
                        setIsMovingLine(false);
                    },
                });
            } else {
                setIsCopyingLine(true);

                copyLines({
                    cartId: cart.id,
                    originalGroupName: currentGroup,
                    newGroupName: newGroup,
                    linesModel,
                    reloadCart: !isQuote,
                    reloadQuote: isQuote,
                    onComplete: (result: any) => {
                        setIsCopyingLine(false);

                        if (result.successful) {
                            setGroupWishListLineIsSelected({
                                wishListLineIds: ids,
                                groupName: currentGroup,
                                clearOnly: true,
                            });
                        }
                    },
                    onError: error => {
                        toasterContext.addToast({ body: translate(error), messageType: "danger" });
                        setIsCopyingLine(false);
                    },
                });
            }
        }
        setCopyToGroup("Choose a Group");
        setMoveToGroup("Choose a Group");
    };

    const setIsCreatingNewGroup = () => {
        setGroupingModalIsOpen({
            isOpen: true,
            shouldLoadCurrentCart: false,
        });
    };

    const modalCloseHandler = () => {
        setGroupingModalIsOpen({
            isOpen: false,
            shouldLoadCurrentCart: false,
        });
        setCopyToGroup("Choose a Group");
        setMoveToGroup("Choose a Group");
    };

    // delete group ->
    const deleteGroupHandler = () => {
        setIsDeleting(true);

        deleteGroup({
            cartId: cart.id,
            originalGroupName: currentGroup,
            reloadCart: !isQuote,
            reloadQuote: isQuote,
            onComplete: () => {
                modalCloseHandler();
                setIsDeleting(false);
            },
            onError: error => {
                toasterContext.addToast({ body: translate(error), messageType: "danger" });
                setIsDeleting(false);
            },
        });
    };

    const copyMoveOptions = [
        "Choose a Group",
        ...(!cart.isSalesperson && isQuote
            ? listGroups.filter(x => x.toLowerCase() !== addOnsGroupName.toLowerCase())
            : listGroups),
        "Create a New Group",
    ];

    if (isQuote && parseBoolean(isExpired)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.groupAcionsWrapper} className={isQuote ? "with-borders" : ""}>
            <StyledWrapper {...styles.selectAllGroup}>
                <Checkbox
                    checked={isAllSelected}
                    onChange={selectAllChangeHandler}
                    data-test-selector="selectAllItems"
                    disabled={
                        isDisabled ||
                        (!cart.isSalesperson && isQuote && currentGroup.toLowerCase() === addOnsGroupName.toLowerCase())
                    }
                >
                    {translate("Select All Products")}
                </Checkbox>
            </StyledWrapper>
            <StyledWrapper {...styles.moveToGroup} className={isDisabled || isMoveCopyDisabled ? "disabled" : ""}>
                <Select
                    label={translate("Move to")}
                    {...styles.selectSortOption}
                    value={moveToGroup}
                    disabled={isDisabled || isMoveCopyDisabled}
                    onChange={handleSelectMoveToGroup}
                    data-test-selector="requestQuote_GroupMoveTo"
                >
                    {copyMoveOptions.length > 0 &&
                        copyMoveOptions
                            .filter(i => i.toLowerCase() !== currentGroup.toLowerCase())
                            .map(option => (
                                <option value={option} key={`${option}_${currentGroup}`}>
                                    {option}
                                </option>
                            ))}
                </Select>
                {isMovingLine && <LoadingSpinner size={28} color="secondary" />}
            </StyledWrapper>
            <StyledWrapper {...styles.moveToGroup} className={isDisabled || isMoveCopyDisabled ? "disabled" : ""}>
                <Select
                    label={translate("Copy to")}
                    {...styles.selectSortOption}
                    value={copyToGroup}
                    disabled={isDisabled || isMoveCopyDisabled}
                    onChange={handleSelectCopyToGroup}
                    data-test-selector="requestQuote_GroupCopyTo"
                >
                    {copyMoveOptions.length > 0 &&
                        copyMoveOptions
                            .filter(i => i.toLowerCase() !== currentGroup.toLowerCase())
                            .map(option => (
                                <option value={option} key={`${option}_${currentGroup}`}>
                                    {option}
                                </option>
                            ))}
                </Select>
                {isCopyingLine && <LoadingSpinner size={28} color="secondary" />}
            </StyledWrapper>
            {currentGroup.toLowerCase() !== uncategorizedGroupName.toLowerCase() &&
                currentGroup.toLowerCase() !== addOnsGroupName.toLowerCase() && (
                    <StyledWrapper {...styles.deleteGroup}>
                        <Link
                            onClick={() => setDeleteGroupModalIsOpen(true)}
                            disabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                            {...styles.deleteGroupLink}
                        >
                            {translate("Delete Group")}
                        </Link>
                    </StyledWrapper>
                )}
            <CreateNewGroupModal
                creatingNewGroup={true}
                groupingModalIsOpen={groupingModalIsOpen.isOpen}
                shouldLoadCurrentCart={groupingModalIsOpen.shouldLoadCurrentCart}
                closeModalCallback={modalCloseHandler}
                callAfterSuccess={handleCopyOrMove}
                isMovingItem={isMove}
                isQuote={isQuote}
                cart={cart}
            />
            <TwoButtonModal
                modalIsOpen={deleteGroupModalIsOpen}
                headlineText={translate("Delete Group")}
                messageText={`${translate("Are you sure you want to delete this group?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteGroupModalIsOpen(false)}
                onSubmit={deleteGroupHandler}
                submitTestSelector="submitDeleteListItem"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(GroupActions));
